// SHADOW MARKET™ — Standalone cart page (mirrors drawer for direct linking).
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Minus, Trash2, ArrowLeft, AlertTriangle } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { useShopI18n } from "../i18n/shop";
import { API, safeJson } from "../lib/api";
import { FREE_SHIPPING_THRESHOLD } from "../data/shop-products";
import { RBLPaymentButton } from "../components/market/RBLPaymentButton";
import "./shadow-market.css";

const ShadowMarketCartPage = () => {
  const { items, removeItem, updateQty, compute } = useCart();
  const { isPro, user, token } = useAuth();
  const { lang } = useLanguage();
  const t = useShopI18n(lang);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const totals = compute(isPro);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleCheckout = async () => {
    if (!items.length) return;
    setLoading(true); setErr(null);
    try {
      const res = await fetch(`${API}/shadow-market/checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({
          items: items.map((i) => ({
            productId: i.id, type: i.type, name: i.name,
            color: i.color, size: i.size, quantity: i.quantity,
          })),
          origin_url: window.location.origin,
          is_pro: !!isPro,
          user_email: user?.email || null,
          user_id: user?.id || null,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await safeJson(res);
      if (data.url) window.location.href = data.url;
    } catch (e) {
      setErr(e.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="shop-root flex min-h-screen flex-col bg-black text-white antialiased" data-testid="shadow-market-cart-page">
      <Navbar />
      <div style={{ height: 96 }} />

      <main className="mx-auto w-full max-w-5xl flex-1 px-6 py-8 md:px-10">
        <Link
          to="/shadow-market"
          className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-white/45 transition-colors hover:text-orange-200"
        >
          <ArrowLeft className="h-3 w-3" /> {t.cart_continue}
        </Link>

        <h1 className="mt-6 text-3xl font-black tracking-tight text-white md:text-4xl">
          {t.cart_title} <span className="text-white/40">({totals.itemCount})</span>
        </h1>

        {items.length === 0 ? (
          <div className="py-20 text-center">
            <p className="text-sm text-white/45">{t.cart_empty}</p>
            <Link
              to="/shadow-market"
              className="mt-4 inline-block text-[11px] font-black uppercase tracking-[0.22em] text-orange-300"
              style={{ borderBottom: "1px solid currentColor", paddingBottom: 2 }}
            >
              {t.cart_empty_cta} →
            </Link>
          </div>
        ) : (
          <div className="mt-8 grid gap-6 lg:grid-cols-3">
            <div className="space-y-3 lg:col-span-2">
              {items.map((it) => {
                const lineUnit = isPro ? it.proPrice : it.price;
                return (
                  <div key={it.key} className="flex gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-4 backdrop-blur-sm">
                    <div
                      className="h-16 w-16 shrink-0 rounded-lg"
                      style={{
                        background: it.colorHex,
                        boxShadow: "0 0 0 1px rgba(255,255,255,0.12), 0 8px 24px rgba(0,0,0,0.5)",
                      }}
                    />
                    <div className="flex-1">
                      <p className="text-sm font-black tracking-tight text-white">{it.name}</p>
                      <p className="mt-1 text-[11px] text-white/45">
                        {it.color} · {t.product_size} {it.size}
                      </p>
                      <div className="mt-3 flex items-center gap-4">
                        <div className="inline-flex items-center overflow-hidden rounded-full border border-white/15">
                          <button onClick={() => updateQty(it.key, -1)} className="px-3 py-1.5 text-white/55 hover:text-white">
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="min-w-[36px] text-center text-sm font-black text-white">{it.quantity}</span>
                          <button onClick={() => updateQty(it.key, 1)} className="px-3 py-1.5 text-white/55 hover:text-white">
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                        <span className="text-base font-black text-white">€{(lineUnit * it.quantity).toFixed(2)}</span>
                      </div>
                    </div>
                    <button onClick={() => removeItem(it.key)} className="self-start p-1 text-white/40 hover:text-rose-400">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>

            <aside className="space-y-3 self-start rounded-2xl border border-white/10 bg-white/[0.02] p-5 backdrop-blur-sm">
              <h3 className="mb-3 border-b border-white/10 pb-2 text-[11px] font-black uppercase tracking-[0.22em] text-white">{t.cart_summary}</h3>
              <Row label={t.cart_subtotal} value={`€${totals.subtotal.toFixed(2)}`} />
              {isPro && totals.discount > 0 && (
                <Row label={t.cart_discount} value={`-€${totals.discount.toFixed(2)}`} valueClass="text-orange-300" />
              )}
              <Row
                label={t.cart_shipping}
                value={totals.shipping === 0 ? t.cart_free : `€${totals.shipping.toFixed(2)}`}
                valueClass={totals.shipping === 0 ? "text-emerald-300" : ""}
              />
              {totals.subtotal < FREE_SHIPPING_THRESHOLD && (
                <p className="text-[10px] text-white/40">{t.cart_add_for_free_shipping(FREE_SHIPPING_THRESHOLD - totals.subtotal)}</p>
              )}
              <div className="mt-2 flex items-center justify-between border-t border-dashed border-white/10 pt-3">
                <span className="text-[11px] font-black uppercase tracking-[0.22em] text-white/55">{t.cart_total}</span>
                <span className="text-xl font-black text-white">€{totals.total.toFixed(2)}</span>
              </div>

              {err && (
                <div className="flex items-start gap-2 rounded-lg border border-rose-500/40 bg-rose-500/[0.08] p-2 text-[11px] text-rose-200">
                  <AlertTriangle className="h-3 w-3 shrink-0" /> <span>{err}</span>
                </div>
              )}

              <button
                onClick={handleCheckout}
                disabled={loading}
                className="shop-btn-primary mt-2 w-full"
                style={{ height: 50, fontSize: "0.78rem", letterSpacing: "0.1em" }}
                data-testid="shop-cart-page-checkout"
              >
                {loading ? t.cart_redirecting : t.cart_checkout}
              </button>
              <RBLPaymentButton />
              <p className="text-center text-[9px] font-bold uppercase tracking-[0.22em] text-white/35">
                {t.cart_secure}
              </p>
            </aside>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

const Row = ({ label, value, valueClass = "" }) => (
  <div className="flex items-center justify-between text-xs">
    <span className="text-white/45">{label}</span>
    <span className={`text-white ${valueClass}`}>{value}</span>
  </div>
);

export default ShadowMarketCartPage;
